/**
 * EPC Form Component
 * Form for creating and editing EPC data
 *
 * @version 2025.2.0
 */
import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Paper,
  Typography,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { Epc, TipoDeRisco, EpcFormData } from '../../../types/epc';
import { centsToReais, reaisToCents } from '../../../utils/financialHelpers';

// Validation schema
const validationSchema = yup.object({
  nome: yup
    .string()
    .required('Nome do EPC é obrigatório')
    .min(3, 'Mínimo de 3 caracteres')
    .max(255, 'Máximo de 255 caracteres'),
  tipoDeRisco: yup
    .string()
    .oneOf(Object.values(TipoDeRisco), 'Tipo de risco inválido')
    .required('Tipo de risco é obrigatório'),
  risco: yup
    .string()
    .required('Descrição do risco é obrigatória')
    .max(255, 'Máximo de 255 caracteres'),
  eSocial: yup.string().max(50, 'Máximo de 50 caracteres'),
  validade: yup
    .string()
    .required('Data de validade é obrigatória'),
  revisao: yup.string().max(50, 'Máximo de 50 caracteres'),
  atenuacao: yup.string().max(100, 'Máximo de 100 caracteres'),
  proporcional: yup.string().max(50, 'Máximo de 50 caracteres'),
  metodo: yup.string().max(255, 'Máximo de 255 caracteres'),
  custo: yup
    .number()
    .nullable()
    .transform((value, original) => (original === '' ? null : value))
    .min(0, 'Custo não pode ser negativo'),
  quantidade: yup
    .number()
    .nullable()
    .transform((value, original) => (original === '' ? null : value))
    .min(0, 'Quantidade não pode ser negativa')
    .integer('Quantidade deve ser um número inteiro'),
  unidade: yup.string().max(50, 'Máximo de 50 caracteres'),
  observacao: yup.string().max(2000, 'Máximo de 2000 caracteres'),
});

interface EPCFormProps {
  initialData?: Partial<Epc>;
  onSubmit: (data: EpcFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const EPCForm: React.FC<EPCFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const isEditing = Boolean(initialData?.id);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EpcFormData>({
    resolver: yupResolver(validationSchema as yup.ObjectSchema<EpcFormData>),
    defaultValues: {
      nome: initialData?.nome || '',
      tipoDeRisco: initialData?.tipoDeRisco || TipoDeRisco.FISICO,
      risco: initialData?.risco || '',
      eSocial: initialData?.eSocial || '',
      validade: initialData?.validade || '',
      revisao: initialData?.revisao || '',
      atenuacao: initialData?.atenuacao || '',
      proporcional: initialData?.proporcional || '',
      metodo: initialData?.metodo || '',
      custo: centsToReais(initialData?.custo),
      quantidade: initialData?.quantidade || undefined,
      unidade: initialData?.unidade || '',
      observacao: initialData?.observacao || '',
    },
  });

  const handleFormSubmit = async (data: EpcFormData) => {
    // Convert custo from reais to centavos if provided
    const submissionData = {
      ...data,
      custo: reaisToCents(data.custo),
    };
    await onSubmit(submissionData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      {/* Identification Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Identificação
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {/* Nome */}
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="nome"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Nome do EPC"
                  fullWidth
                  required
                  error={Boolean(errors.nome)}
                  helperText={errors.nome?.message || 'Ex: Enclausuramento de Máquinas'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          {/* Tipo de Risco */}
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <FormControl fullWidth required error={Boolean(errors.tipoDeRisco)}>
              <InputLabel>Tipo de Risco</InputLabel>
              <Controller
                name="tipoDeRisco"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="Tipo de Risco" disabled={isSubmitting}>
                    <MenuItem value={TipoDeRisco.FISICO}>Físico</MenuItem>
                    <MenuItem value={TipoDeRisco.QUIMICO}>Químico</MenuItem>
                    <MenuItem value={TipoDeRisco.BIOLOGICO}>Biológico</MenuItem>
                    <MenuItem value={TipoDeRisco.ERGONOMICO}>Ergonômico</MenuItem>
                    <MenuItem value={TipoDeRisco.ACIDENTE}>Acidente</MenuItem>
                  </Select>
                )}
              />
              {errors.tipoDeRisco && <FormHelperText>{errors.tipoDeRisco.message}</FormHelperText>}
            </FormControl>
          </Grid>

          {/* Revisão */}
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <Controller
              name="revisao"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Revisão"
                  fullWidth
                  error={Boolean(errors.revisao)}
                  helperText={errors.revisao?.message || 'Ex: Rev. 01'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          {/* Risco */}
          <Grid size={12}>
            <Controller
              name="risco"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Descrição do Risco Controlado"
                  fullWidth
                  required
                  error={Boolean(errors.risco)}
                  helperText={errors.risco?.message || 'Ex: Partes móveis de máquinas'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Validity Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Validade
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {/* Validade */}
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="validade"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Data de Validade"
                  fullWidth
                  required
                  type="date"
                  slotProps={{ inputLabel: { shrink: true } }}
                  error={Boolean(errors.validade)}
                  helperText={errors.validade?.message || 'Data de vencimento/manutenção'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          {/* eSocial Code */}
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="eSocial"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Código eSocial"
                  fullWidth
                  error={Boolean(errors.eSocial)}
                  helperText={errors.eSocial?.message || 'Código da tabela eSocial (S-2240)'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Technical Information Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Informações Técnicas
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {/* Atenuação */}
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="atenuacao"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Fator de Atenuação"
                  fullWidth
                  error={Boolean(errors.atenuacao)}
                  helperText={errors.atenuacao?.message || 'Ex: Redução de 15 dB'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          {/* Proporcional */}
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <FormControl fullWidth>
              <InputLabel>Proporcional</InputLabel>
              <Controller
                name="proporcional"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="Proporcional" disabled={isSubmitting}>
                    <MenuItem value="">Não especificado</MenuItem>
                    <MenuItem value="Sim">Sim - Redução igual para todos</MenuItem>
                    <MenuItem value="Não">Não - Redução variável</MenuItem>
                  </Select>
                )}
              />
              <FormHelperText>A redução é igual para todos os trabalhadores?</FormHelperText>
            </FormControl>
          </Grid>

          {/* Método */}
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="metodo"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Método de Operação"
                  fullWidth
                  error={Boolean(errors.metodo)}
                  helperText={errors.metodo?.message || 'Ex: Automático, Manual'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Stock Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Estoque e Custos
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {/* Quantidade */}
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="quantidade"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  label="Quantidade Instalada"
                  fullWidth
                  type="number"
                  error={Boolean(errors.quantidade)}
                  helperText={errors.quantidade?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          {/* Unidade */}
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="unidade"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Unidade de Medida"
                  fullWidth
                  error={Boolean(errors.unidade)}
                  helperText={errors.unidade?.message || 'Ex: unidade, metro, m²'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          {/* Custo */}
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="custo"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ? field.value / 100 : ''}
                  onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) * 100 : null)}
                  label="Custo Unitário"
                  fullWidth
                  type="number"
                  slotProps={{
                    input: {
                      startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    },
                  }}
                  error={Boolean(errors.custo)}
                  helperText={errors.custo?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Observations Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Observações
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          <Grid size={12}>
            <Controller
              name="observacao"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Observações"
                  fullWidth
                  multiline
                  rows={4}
                  error={Boolean(errors.observacao)}
                  helperText={errors.observacao?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          type="button"
          onClick={onCancel}
          variant="outlined"
          startIcon={<CancelIcon />}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>

        <Button
          type="submit"
          variant="contained"
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={isSubmitting}
        >
          {isEditing ? 'Atualizar' : 'Criar'} EPC
        </Button>
      </Box>
    </Box>
  );
};

export default EPCForm;
